import { useCallback } from "react";
import { listConnectors, type ListedConnector } from "../../../api/connectorsApi";
import type { AgentsAtOnce } from "../../../api/connectorStatus";
import { useSession } from "../../../session/sessionContext";
import { useLoad } from "../../../shared/loading/useLoad";
import { useRefreshWhile } from "../../connectors/setup/useConnectorSetup";
import { isAnotherPc, readThisPcMark, type ThisPcMark } from "../../connectors/thisPc/thisPc";

const NO_CONNECTORS: readonly ListedConnector[] = [];

/** Where the host approves a tool version: the PC whose connector hosts the Thread. */
export interface ApprovalTarget {
  readonly connectorId: string;
  /** The PC's name, as its connector reports it. */
  readonly pc: string;
  /** Its own page runs, so the server can ask it to open there (F85). */
  readonly pageServed: boolean;
  /** This browser is known to be on another PC (the person said which, F78), so the Thread says so (TA-6). */
  readonly elsewhere: boolean;
  /** How many agents its owner lets run there at once (CN-14), for a turn that waits for a slot; `null` before a report. */
  readonly agentsAtOnce: AgentsAtOnce | null;
  /** Each agent there by what it calls itself, where it said, for a problem that names it (CW-1). */
  readonly agentTitles: Readonly<Record<string, string>>;
}

/**
 * The approval target for the Thread's connector (connector.md TA-4), when that connector is
 * the viewer's own, so the viewer hosts it, and online: the server relays to it. Pure.
 */
export function approvalTargetOf(ownConnectors: readonly ListedConnector[], connectorId: string | null, mark: ThisPcMark): ApprovalTarget | null {
  const connector = ownConnectors.find((candidate) => candidate.connectorId === connectorId && candidate.status.online);
  if (connector === undefined) return null;
  return {
    connectorId: connector.connectorId,
    pc: connector.name,
    pageServed: connector.status.approvalPageUrl !== null,
    elsewhere: isAnotherPc(ownConnectors, mark, connector.connectorId),
    agentsAtOnce: connector.status.agentsAtOnce,
    agentTitles: Object.fromEntries(connector.status.agents.flatMap((agent) => (agent.title === null ? [] : [[agent.agentId, agent.title]]))),
  };
}

/** How often a waiting turn reads its PC again, so a changed agents at once shows (CN-14). */
const WHILE_WAITING_MILLISECONDS = 5_000;

/**
 * The approval target to offer for the Thread's connector, or `null` (also while loading);
 * read again every few seconds while `refreshWhile`, as a turn waits on that PC.
 */
export function useApprovalTarget(connectorId: string | null, refreshWhile = false): ApprovalTarget | null {
  const { session } = useSession();
  const userId = session.status === "signedIn" ? session.session.account.userId : null;
  const load = useCallback(
    (signal: AbortSignal) => (connectorId === null ? Promise.resolve(NO_CONNECTORS) : listConnectors(signal)),
    [connectorId],
  );
  const { state, refresh } = useLoad(load);
  useRefreshWhile(refreshWhile && connectorId !== null, WHILE_WAITING_MILLISECONDS, refresh);
  return state.status === "loaded" ? approvalTargetOf(state.value, connectorId, userId === null ? null : readThisPcMark(userId)) : null;
}
